import FulfillmentType from "@/enum/FulfillmentType";
import ShopType from "@/enum/ShopType";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { isReissueSupported, isReshipSupported, preShipShopTypes } from "@/utils/ShopUtils";
import { autoPrintSupportShopTypes } from "@/scenes/more/AutoPrint/constant";

export enum OperationKey {
    HOLD_ORDER = "holdOrder",
    BATCH_MERGE_ORDER = "batchMergeOrder",
    BATCH_CANCEL_MERGE_ORDER = "batchCancelMergeOrder",
    SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK = "supplierBatchModifyDropShippingRemark",
    UPDATE_SELLER_REMARKS = "updateSellerRemarks",
    ORDER_MARK = "orderMark",
    SPLIT_ORDER = "splitOrder",
    CANCEL_SPLIT_ORDER = "cancelSplitOrder",
    SUPPLIER_BATCH_SYNC_ORDERS = "supplierBatchSyncOrders",
    BATCH_SYNC_ORDERS = "batchSyncOrders",
    BATCH_UPDATE_IMEI = "batchUpdateIMEI",
    PRINT_WAYBILL = "printWaybill",
    PRINT_PACKING_LIST = "printPackingList",
    REISSUE_PACKAGE = "reissuePackage",
    PRE_SHIP = "preShip",
    RESHIP = "reship",
    PRINT_ORDER_ITEM_LABEL = "printOrderItemLabel",
    BATCH_REPRINT_EWAYBILLS = "batchReprintEwaybills",
    BATCH_CANCEL_EWAYBILL_NOS = "batchCancelEwaybillNos",
    BATCH_DELETE_EWAYBILL_NOS = "batchDeleteEwaybillNos",
    DELETE_WAYBILL_NO = "deleteWaybillNo",
    UPDATE_LOGISTICS_COMPANY = "updateLogisticsCompany",
    UPDATE_PRINTING_STATUS = "updatePrintingStatus",
    AUTO_PRINT = "autoPrint",
    INSTANT_SHIP = "instantShip",
    SCAN_PRINT = "scanPrint",
    PDD_FDS_EWAYBILL_CANCEL = "pddFdsEwaybillCancel",
    QUALITY_INSPECTION_CODE = "qualityInspectionCode",
    EXPORT_PICK_LIST = "exportPickList",
    EXPORT_ORDER = "exportOrder",
    EXPORT_EXPRESS = "exportExpress",
    EXPORT_SHIPPING_TEMPLATE = "exportShippingTemplate",
    EXPORT_PRINT_PURCHASING_LABEL = "exportPrintPurchasingLabel",
    DOWNLOAD_CUSTOMIZATION = "downloadCustomization",
    CANCEL_DROPSHIPPING_ORDER = "cancelDropshippingOrder",
    BATCH_LOCK = "batchLock",
    BATCH_UNLOCK = "batchUnlock",
    COPY_ORDER_INFO = "copyOrderInfo",
    BATCH_UPDATE_SHIPPER = "batchUpdateShipper",
    BATCH_SUBMIT_ORDER = "batchSubmitOrder",
    PUSH_ORDER_TO_FACTORY = "pushOrderToFactory",
    BATCH_PAY_ORDER = "batchPayOrder",
    BATCH_REFUND = "batchRefund",
}

export const OperationLabelClsMap = {
    [OperationKey.PRINT_PACKING_LIST]: "ant-btn-success",
    [OperationKey.RESHIP]: "ant-btn-warning",
    [OperationKey.EXPORT_SHIPPING_TEMPLATE]: "ant-btn-gray2",
};

export const OperationLabelNameMap: Record<OperationKey, string> = {
    [OperationKey.HOLD_ORDER]: "挂起订单",
    [OperationKey.BATCH_MERGE_ORDER]: "自动合并订单",
    [OperationKey.BATCH_CANCEL_MERGE_ORDER]: "取消合并订单",
    [OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK]: "修改代发备注",
    [OperationKey.UPDATE_SELLER_REMARKS]: "修改备注、旗帜",
    [OperationKey.ORDER_MARK]: "标记订单",
    [OperationKey.SPLIT_ORDER]: "拆分订单",
    [OperationKey.CANCEL_SPLIT_ORDER]: "取消拆分订单",
    [OperationKey.SUPPLIER_BATCH_SYNC_ORDERS]: "重新同步订单",
    [OperationKey.BATCH_SYNC_ORDERS]: "重新同步订单",
    [OperationKey.BATCH_UPDATE_IMEI]: "录入商品串码(IMEI码)/SN码/69码",
    [OperationKey.PRINT_WAYBILL]: "打印快递单",
    [OperationKey.PRINT_PACKING_LIST]: "打印发货单",
    [OperationKey.REISSUE_PACKAGE]: "补发包裹",
    [OperationKey.RESHIP]: "修改单号(重新发货)",
    [OperationKey.PRINT_ORDER_ITEM_LABEL]: "打印订单商品标签",
    [OperationKey.BATCH_REPRINT_EWAYBILLS]: "重打旧电子面单号",
    [OperationKey.BATCH_CANCEL_EWAYBILL_NOS]: "回收电子面单号",
    [OperationKey.BATCH_DELETE_EWAYBILL_NOS]: "删除电子面单号",
    [OperationKey.DELETE_WAYBILL_NO]: "删除运单号",
    [OperationKey.UPDATE_LOGISTICS_COMPANY]: "修改物流公司",
    [OperationKey.UPDATE_PRINTING_STATUS]: "修改打印状态",
    [OperationKey.AUTO_PRINT]: "自动打印",
    [OperationKey.INSTANT_SHIP]: "极速发货",
    [OperationKey.SCAN_PRINT]: "扫描打印",
    [OperationKey.PDD_FDS_EWAYBILL_CANCEL]: "取消回传运单号",
    [OperationKey.QUALITY_INSPECTION_CODE]: "打印质检码",
    [OperationKey.EXPORT_PICK_LIST]: "导出备货单",
    [OperationKey.EXPORT_ORDER]: "订单导出",
    [OperationKey.EXPORT_EXPRESS]: "导出快递运单号",
    [OperationKey.EXPORT_SHIPPING_TEMPLATE]: "导出发货模板",
    [OperationKey.EXPORT_PRINT_PURCHASING_LABEL]: "导出/打印拿货标签",
    [OperationKey.DOWNLOAD_CUSTOMIZATION]: "下载定制图",
    [OperationKey.CANCEL_DROPSHIPPING_ORDER]: "取消订单",
    [OperationKey.BATCH_LOCK]: "批量锁定",
    [OperationKey.BATCH_UNLOCK]: "批量解锁",
    [OperationKey.COPY_ORDER_INFO]: "复制订单信息",
    [OperationKey.BATCH_UPDATE_SHIPPER]: "修改发货人",
    [OperationKey.PRE_SHIP]: "预发货",
    [OperationKey.BATCH_SUBMIT_ORDER]: "提交订单",
    [OperationKey.PUSH_ORDER_TO_FACTORY]: "推送订单到工厂",
    [OperationKey.BATCH_PAY_ORDER]: "支付代发订单",
    [OperationKey.BATCH_REFUND]: "退回代发订单",
};

export interface OtherOperationContext {
    tab: OrderTab;
    shop: ShopDTO;
    isSupplierShop: boolean;
    isRetailer: boolean;
    fulfillmentType?: FulfillmentType;
    showPackListExport: boolean;
    showBatchSubmitOrder: boolean;
    showBatchPayOrder: boolean;
    companyId?: number;
}

export const DEFAULT_FAVORITE_OPERATION_KEYS: OperationKey[] = [OperationKey.PRINT_PACKING_LIST, OperationKey.PRE_SHIP];

const filterKnownKeys = (keys: string[]) => {
    return keys.filter((key) => key in OperationLabelNameMap);
};

export const getAvailableOtherOperationKeys = (context: OtherOperationContext): string[] => {
    const {
        tab,
        shop,
        isSupplierShop,
        isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
        companyId,
    } = context;

    if (!shop || tab == null) {
        return [];
    }

    if (tab === OrderTab.PRE_SHIP) {
        return [OperationKey.EXPORT_PICK_LIST, OperationKey.EXPORT_ORDER];
    }

    const shopType = shop.type;

    const showDownloadCustomization = ![
        ShopType.PDD_FDS,
        ShopType.DOUDIAN_IOP,
        ShopType.DOUDIAN_SUPPLIER,
        ShopType.KWAI_DROPSHIP,
        ShopType.PDD_FULFILLMENT,
        ShopType.CHANNEL_SHOP_DROPSHIP,
        ShopType.CHANNEL_SHOP_SUPPLIER,
    ].includes(shopType);
    const showExportShippingTemplate = [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shopType);

    const keys: string[] = [];

    const showPreShip = tab === OrderTab.PENDING_SHIPMENT && preShipShopTypes.includes(shopType);
    const showReissuePackage = [OrderTab.SUCCESS, OrderTab.SHIPPED].includes(tab) && isReissueSupported(shop);
    const showModifyWaybill = tab === OrderTab.SHIPPED && isReshipSupported(shop);

    if (isSupplierShop) {
        if (tab === OrderTab.PENDING_SHIPMENT) {
            keys.push(OperationKey.HOLD_ORDER, OperationKey.BATCH_MERGE_ORDER);
        }

        keys.push(
            OperationKey.BATCH_CANCEL_MERGE_ORDER,
            OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK,
            OperationKey.ORDER_MARK,
        );

        if (showPreShip) {
            keys.push(OperationKey.PRE_SHIP);
        }

        if ([OrderTab.PENDING_SHIPMENT, OrderTab.PENDING_SHIPMENT_REFUNDING].includes(tab)) {
            keys.push(OperationKey.SPLIT_ORDER);
        }

        if (tab === OrderTab.PENDING_SHIPMENT) {
            keys.push(OperationKey.CANCEL_SPLIT_ORDER);
        }

        keys.push(OperationKey.SUPPLIER_BATCH_SYNC_ORDERS);

        keys.push(OperationKey.PRINT_PACKING_LIST);

        if (showReissuePackage) {
            keys.push(OperationKey.REISSUE_PACKAGE);
        }

        if (showModifyWaybill) {
            keys.push(OperationKey.RESHIP);
        }

        keys.push(
            OperationKey.PRINT_ORDER_ITEM_LABEL,
            OperationKey.BATCH_REPRINT_EWAYBILLS,
            OperationKey.BATCH_CANCEL_EWAYBILL_NOS,
            OperationKey.BATCH_DELETE_EWAYBILL_NOS,
            OperationKey.UPDATE_LOGISTICS_COMPANY,
            OperationKey.UPDATE_PRINTING_STATUS,
        );

        keys.push(OperationKey.SCAN_PRINT);

        if (showPackListExport) {
            keys.push(OperationKey.EXPORT_PICK_LIST);
        }

        if (showExportShippingTemplate) {
            keys.push(OperationKey.EXPORT_SHIPPING_TEMPLATE);
        }

        keys.push(OperationKey.EXPORT_ORDER, OperationKey.EXPORT_EXPRESS, OperationKey.EXPORT_PRINT_PURCHASING_LABEL);

        if (showDownloadCustomization) {
            keys.push(OperationKey.DOWNLOAD_CUSTOMIZATION);
        }

        if ([OrderTab.PENDING_SHIPMENT, OrderTab.SHIPPED].includes(tab)) {
            keys.push(OperationKey.CANCEL_DROPSHIPPING_ORDER);
        }

        keys.push(OperationKey.BATCH_LOCK, OperationKey.BATCH_UNLOCK, OperationKey.COPY_ORDER_INFO);

        return filterKnownKeys(keys);
    }

    if (tab === OrderTab.PENDING_SHIPMENT) {
        keys.push(OperationKey.HOLD_ORDER, OperationKey.BATCH_MERGE_ORDER);
    }

    if (shopType !== ShopType.PDD_FULFILLMENT) {
        keys.push(OperationKey.BATCH_CANCEL_MERGE_ORDER);
    }

    keys.push(OperationKey.UPDATE_SELLER_REMARKS, OperationKey.ORDER_MARK);

    if (showPreShip) {
        keys.push(OperationKey.PRE_SHIP);
    }

    if (
        [OrderTab.PENDING_SHIPMENT, OrderTab.PENDING_SHIPMENT_REFUNDING].includes(tab) &&
        shopType !== ShopType.PDD_FULFILLMENT
    ) {
        keys.push(OperationKey.SPLIT_ORDER);
    }

    if (tab === OrderTab.PENDING_SHIPMENT && shopType !== ShopType.PDD_FULFILLMENT) {
        keys.push(OperationKey.CANCEL_SPLIT_ORDER);
    }

    if (!isSupplierShop && ![ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shopType)) {
        keys.push(OperationKey.BATCH_SYNC_ORDERS);
    }

    if (
        [ShopType.TAOBAO, ShopType.PINDUODUO, ShopType.DOUDIAN, ShopType.SUNING, ShopType.JD, ShopType.KWAI].includes(
            shopType,
        )
    ) {
        keys.push(OperationKey.BATCH_UPDATE_IMEI);
    }

    keys.push(OperationKey.PRINT_PACKING_LIST);

    if (showReissuePackage) {
        keys.push(OperationKey.REISSUE_PACKAGE);
    }

    if (showModifyWaybill) {
        keys.push(OperationKey.RESHIP);
    }

    keys.push(
        OperationKey.PRINT_ORDER_ITEM_LABEL,
        OperationKey.BATCH_REPRINT_EWAYBILLS,
        OperationKey.BATCH_CANCEL_EWAYBILL_NOS,
        OperationKey.BATCH_DELETE_EWAYBILL_NOS,
        OperationKey.UPDATE_LOGISTICS_COMPANY,
        OperationKey.UPDATE_PRINTING_STATUS,
    );

    if (autoPrintSupportShopTypes.includes(shopType)) {
        keys.push(OperationKey.AUTO_PRINT);
    }

    if (shopType === ShopType.DOUDIAN) {
        keys.push(OperationKey.INSTANT_SHIP);
    }

    keys.push(OperationKey.SCAN_PRINT);

    if (shopType === ShopType.PDD_FDS && tab === OrderTab.SHIPPED) {
        keys.push(OperationKey.PDD_FDS_EWAYBILL_CANCEL);
    }

    if (
        [ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shopType)
    ) {
        keys.push(OperationKey.QUALITY_INSPECTION_CODE);
    }

    if (showPackListExport) {
        keys.push(OperationKey.EXPORT_PICK_LIST);
    }

    if (showExportShippingTemplate) {
        keys.push(OperationKey.EXPORT_SHIPPING_TEMPLATE);
    }

    keys.push(OperationKey.EXPORT_ORDER, OperationKey.EXPORT_EXPRESS, OperationKey.EXPORT_PRINT_PURCHASING_LABEL);

    if (showDownloadCustomization) {
        keys.push(OperationKey.DOWNLOAD_CUSTOMIZATION);
    }

    const hideRetailerGroups = [
        OrderTab.SHIPPED,
        OrderTab.SHIPPED_REFUNDING,
        OrderTab.SUCCESS,
        OrderTab.CANCELED,
        OrderTab.PENDING_SHIPMENT_REFUNDING,
    ].includes(tab);

    if (!hideRetailerGroups) {
        if (isRetailer) {
            if (showBatchSubmitOrder) {
                keys.push(OperationKey.BATCH_SUBMIT_ORDER);
            }

            if (companyId === 162105) {
                keys.push(OperationKey.PUSH_ORDER_TO_FACTORY);
            }

            if (showBatchPayOrder) {
                keys.push(OperationKey.BATCH_PAY_ORDER);
            }

            if (fulfillmentType === FulfillmentType.DROP_SHIPPING) {
                keys.push(OperationKey.BATCH_REFUND);
            }
        }

        keys.push(OperationKey.BATCH_LOCK, OperationKey.BATCH_UNLOCK);

        if ([ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shopType)) {
            keys.push(OperationKey.BATCH_UPDATE_SHIPPER);
        }

        keys.push(OperationKey.COPY_ORDER_INFO);
    } else {
        keys.push(OperationKey.COPY_ORDER_INFO);
    }

    return filterKnownKeys(keys);
};
